import React, { useCallback, useMemo, useState } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import Field from '../../../../../components/Field';
import TextInput from '../../../../../components/TextInput';
import Toggle from '../../../../../components/Toggle';
import PanelHeading from '../../../../../components/PanelHeading';
import StatusPill, { PillTone } from '../../../../../components/StatusPill';
import IconButton from '../../../../../components/IconButton';
import { EditIcon, TrashIcon } from '../../../../../components/IconButton/icons';
import { formatHoursShort } from '../../../../../utils/timesheetTime';
import {
  DEFAULT_LEAVE_TERMS,
  EmployeeLeave,
  FormErrors,
  LeaveEntitlement,
  LeaveEntitlementInput,
  LeaveSettingsDraft
} from '../../../config';
import { formatDay, parseNumber } from './leaveFormat';

interface LeaveEntitlementSectionProps {
  leave: EmployeeLeave;
  draft: LeaveSettingsDraft;
  errors: FormErrors;
  disabled: boolean;
  canEdit: boolean;
  onDraftChange: (patch: Partial<LeaveSettingsDraft>) => void;
  onSaveEntitlement: (input: LeaveEntitlementInput) => Promise<string | null>;
  onDeleteEntitlement: (entitlement: LeaveEntitlement) => void;
}

/** One stretch of the entitlement history, with the dates it ran between. */
interface TimelineRow {
  key: string;
  /** null: nothing saved for this stretch, so the defaults apply. */
  saved: LeaveEntitlement | null;
  /** True for the entitlement that applies from the start (no date of its own). */
  fromStart: boolean;
  from: string | null;
  to: string | null;
  contracted_hours_per_week: number;
  annual_leave_weeks_per_year: number;
  sick_leave_days_per_year: number;
  accrues: boolean;
  note: string | null;
  status: { label: string; tone: PillTone };
}

interface EntitlementForm {
  /** Editing an existing row keeps its date: the date is what identifies it. */
  mode: 'add' | 'edit';
  fromStart: boolean;
  effective_from: string;
  contracted_hours_per_week: string;
  annual_leave_weeks_per_year: string;
  sick_leave_days_per_year: string;
  accrues: boolean;
  note: string;
}

interface EntitlementErrors {
  effective_from?: string;
  contracted_hours_per_week?: string;
  annual_leave_weeks_per_year?: string;
  sick_leave_days_per_year?: string;
  general?: string;
}

const dayBefore = (date: string): string => format(subDays(parseISO(date), 1), 'yyyy-MM-dd');

const inRange = (value: number | null, max: number): value is number =>
  value !== null && value >= 0 && value <= max;

const statusOf = (
  row: { accrues: boolean; from: string | null; to: string | null; saved: LeaveEntitlement | null },
  today: string
): TimelineRow['status'] => {
  if (!row.accrues) return { label: 'Not earning', tone: 'neutral' };
  if (row.saved?.paused) return { label: 'Paused', tone: 'neutral' };
  if (row.from && row.from > today) return { label: 'Upcoming', tone: 'info' };
  if (row.to && row.to < today) return { label: 'Ended', tone: 'neutral' };
  if (!row.saved) return { label: 'Default', tone: 'warning' };
  return { label: 'Current', tone: 'success' };
};

/**
 * The entitlement history as one unbroken run of stretches, oldest to newest.
 *
 * Only the start of each stretch is stored. Its end is the day before the next
 * one starts, so two stretches can never overlap and there is never a gap
 * between them: adding a row is what ends the row before it.
 */
const buildTimeline = (leave: EmployeeLeave): TimelineRow[] => {
  const today = leave.as_of;
  const dated = leave.entitlements
    .filter((row) => row.effective_from !== null)
    .sort((a, b) => ((a.effective_from as string) < (b.effective_from as string) ? -1 : 1));
  const baseline = leave.entitlements.find((row) => row.effective_from === null) ?? null;
  const start = leave.accrual_start;
  const first = dated[0]?.effective_from ?? null;

  const rows: TimelineRow[] = [];

  // The stretch from the start is shown unless a dated row covers the start
  // already. With nothing saved for it, it runs on the defaults -- shown, so
  // that what the balance was worked out from is never hidden.
  if (!first || !start || first > start) {
    const terms = baseline ?? { ...DEFAULT_LEAVE_TERMS, accrues: true, note: null };
    const partial = {
      saved: baseline,
      from: start,
      to: first ? dayBefore(first) : null,
      accrues: terms.accrues
    };
    rows.push({
      key: 'start',
      fromStart: true,
      contracted_hours_per_week: Number(terms.contracted_hours_per_week),
      annual_leave_weeks_per_year: Number(terms.annual_leave_weeks_per_year),
      sick_leave_days_per_year: Number(terms.sick_leave_days_per_year),
      note: terms.note,
      ...partial,
      status: statusOf(partial, today)
    });
  }

  dated.forEach((row, index) => {
    const next = dated[index + 1]?.effective_from ?? null;
    const partial = {
      saved: row,
      from: row.effective_from,
      to: next ? dayBefore(next) : null,
      accrues: row.accrues
    };
    rows.push({
      key: `row-${row.id}`,
      fromStart: false,
      contracted_hours_per_week: Number(row.contracted_hours_per_week),
      annual_leave_weeks_per_year: Number(row.annual_leave_weeks_per_year),
      sick_leave_days_per_year: Number(row.sick_leave_days_per_year),
      note: row.note,
      ...partial,
      status: statusOf(partial, today)
    });
  });

  return rows;
};

/**
 * What this person earns leave on, as a history: one row per stretch, each
 * with the dates it ran between, like the charge rate history. Full time,
 * then part time for a year, then full time again is three rows.
 *
 * A row saves when it is added. It is not part of the modal's Save, which
 * only holds the opening balance below.
 */
const LeaveEntitlementSection: React.FC<LeaveEntitlementSectionProps> = ({
  leave,
  draft,
  errors,
  disabled,
  canEdit,
  onDraftChange,
  onSaveEntitlement,
  onDeleteEntitlement
}) => {
  const [form, setForm] = useState<EntitlementForm | null>(null);
  const [formErrors, setFormErrors] = useState<EntitlementErrors>({});
  const [saving, setSaving] = useState<boolean>(false);

  const timeline = useMemo(() => buildTimeline(leave), [leave]);
  // Newest first, as the charge rate history reads.
  const newestFirst = useMemo(() => [...timeline].reverse(), [timeline]);

  const openAdd = useCallback(() => {
    // Start from the latest terms, so a change of hours alone is one edit.
    const latest = timeline[timeline.length - 1];
    setForm({
      mode: 'add',
      fromStart: false,
      // The database's today, not the browser's, which can be a day ahead.
      effective_from: leave.as_of,
      contracted_hours_per_week: String(latest?.contracted_hours_per_week ?? DEFAULT_LEAVE_TERMS.contracted_hours_per_week),
      annual_leave_weeks_per_year: String(latest?.annual_leave_weeks_per_year ?? DEFAULT_LEAVE_TERMS.annual_leave_weeks_per_year),
      sick_leave_days_per_year: String(latest?.sick_leave_days_per_year ?? DEFAULT_LEAVE_TERMS.sick_leave_days_per_year),
      // Off for someone inactive: a row added to record their hours must not
      // be what starts them earning again.
      accrues: leave.is_active,
      note: ''
    });
    setFormErrors({});
  }, [timeline, leave.as_of, leave.is_active]);

  const openEdit = useCallback((row: TimelineRow) => {
    setForm({
      mode: 'edit',
      fromStart: row.fromStart,
      effective_from: row.fromStart ? '' : row.from ?? '',
      contracted_hours_per_week: String(row.contracted_hours_per_week),
      annual_leave_weeks_per_year: String(row.annual_leave_weeks_per_year),
      sick_leave_days_per_year: String(row.sick_leave_days_per_year),
      accrues: row.accrues,
      note: row.note ?? ''
    });
    setFormErrors({});
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    const hours = parseNumber(form.contracted_hours_per_week);
    const weeks = parseNumber(form.annual_leave_weeks_per_year);
    const days = parseNumber(form.sick_leave_days_per_year);

    const next: EntitlementErrors = {};
    if (!form.fromStart && !form.effective_from) next.effective_from = 'Enter the date it starts';
    if (
      form.mode === 'add' &&
      leave.entitlements.some((row) => row.effective_from === form.effective_from)
    ) {
      next.effective_from = 'An entitlement already starts on this date. Edit that one instead.';
    }
    if (!inRange(hours, 168)) next.contracted_hours_per_week = 'Between 0 and 168';
    if (!inRange(weeks, 52)) next.annual_leave_weeks_per_year = 'Between 0 and 52';
    if (!inRange(days, 365)) next.sick_leave_days_per_year = 'Between 0 and 365';
    if (Object.keys(next).length > 0 || !inRange(hours, 168) || !inRange(weeks, 52) || !inRange(days, 365)) {
      setFormErrors(next);
      return;
    }

    setSaving(true);
    const failure = await onSaveEntitlement({
      effective_from: form.fromStart ? null : form.effective_from,
      contracted_hours_per_week: hours,
      annual_leave_weeks_per_year: weeks,
      sick_leave_days_per_year: days,
      accrues: form.accrues,
      note: form.note.trim()
    });
    setSaving(false);

    if (failure) {
      setFormErrors({ general: failure });
      return;
    }
    setForm(null);
  }, [form, leave.entitlements, onSaveEntitlement]);

  const startHint = leave.first_timesheet_week
    ? `Leave blank to start with their first timesheet week, ${formatDay(leave.first_timesheet_week)}.`
    : 'They have no timesheet weeks yet, so nothing is earned until a date is set.';

  return (
    <>
      <PanelHeading
        title="Entitlement"
        spaced
        meta={timeline.length}
        hint="What leave is earned on, and when it changed. Leave is earned each fortnight on contracted hours, so part-time hours earn in proportion. Adding a row ends the one before it the day before."
        action={canEdit && !form ? (
          <button type="button" className="panel-heading__action" onClick={openAdd}>
            + Add entitlement
          </button>
        ) : undefined}
      />

      {form && (
        <form className="disclosure-card employee-tab__disclosure" onSubmit={handleSubmit}>
          {formErrors.general && (
            <p className="field__error" role="alert">{formErrors.general}</p>
          )}
          <div className="pdm-grid pdm-grid--4">
            <Field
              label="From"
              htmlFor="leave-ent-from"
              required={!form.fromStart}
              error={formErrors.effective_from}
              hint={
                form.fromStart
                  ? 'Applies from the day leave starts.'
                  : form.mode === 'edit'
                    ? 'To move the date, remove this row and add it again.'
                    : undefined
              }
            >
              {form.fromStart ? (
                <TextInput id="leave-ent-from" value="The start" disabled readOnly />
              ) : (
                <TextInput
                  id="leave-ent-from"
                  type="date"
                  value={form.effective_from}
                  invalid={!!formErrors.effective_from}
                  disabled={saving || form.mode === 'edit'}
                  onChange={(e) => setForm((prev) => prev && { ...prev, effective_from: e.target.value })}
                />
              )}
            </Field>
            <Field
              label="Hours per week"
              htmlFor="leave-ent-hours"
              required
              error={formErrors.contracted_hours_per_week}
              hint="37.5 is full time."
            >
              <TextInput
                id="leave-ent-hours"
                type="number"
                step="0.25"
                min="0"
                max="168"
                value={form.contracted_hours_per_week}
                invalid={!!formErrors.contracted_hours_per_week}
                disabled={saving}
                onChange={(e) => setForm((prev) => prev && { ...prev, contracted_hours_per_week: e.target.value })}
              />
            </Field>
            <Field
              label="Annual leave, weeks a year"
              htmlFor="leave-ent-weeks"
              required
              error={formErrors.annual_leave_weeks_per_year}
              hint="4 is the standard."
            >
              <TextInput
                id="leave-ent-weeks"
                type="number"
                step="0.5"
                min="0"
                max="52"
                value={form.annual_leave_weeks_per_year}
                invalid={!!formErrors.annual_leave_weeks_per_year}
                disabled={saving}
                onChange={(e) => setForm((prev) => prev && { ...prev, annual_leave_weeks_per_year: e.target.value })}
              />
            </Field>
            <Field
              label="Sick leave, days a year"
              htmlFor="leave-ent-days"
              required
              error={formErrors.sick_leave_days_per_year}
              hint="10 is the standard."
            >
              <TextInput
                id="leave-ent-days"
                type="number"
                step="0.5"
                min="0"
                max="365"
                value={form.sick_leave_days_per_year}
                invalid={!!formErrors.sick_leave_days_per_year}
                disabled={saving}
                onChange={(e) => setForm((prev) => prev && { ...prev, sick_leave_days_per_year: e.target.value })}
              />
            </Field>
            <Field label="Reason" htmlFor="leave-ent-note" className="span-2">
              <TextInput
                id="leave-ent-note"
                value={form.note}
                disabled={saving}
                placeholder="e.g. Moved to three days a week"
                onChange={(e) => setForm((prev) => prev && { ...prev, note: e.target.value })}
              />
            </Field>
            <Field
              label="Earns leave"
              className="span-2"
              hint="Switch off for a stretch that earns nothing, such as unpaid leave."
            >
              <Toggle
                checked={form.accrues}
                disabled={saving}
                label={form.accrues ? 'Earning' : 'Not earning'}
                onChange={(checked) => setForm((prev) => prev && { ...prev, accrues: checked })}
              />
            </Field>
          </div>
          <div className="disclosure-card__actions">
            <button
              type="button"
              className="pill-btn pill-btn--ghost pill-btn--sm"
              disabled={saving}
              onClick={() => setForm(null)}
            >
              Cancel
            </button>
            <button type="submit" className="pill-btn pill-btn--primary pill-btn--sm" disabled={saving}>
              {saving ? 'Saving…' : form.mode === 'edit' ? 'Save entitlement' : 'Add entitlement'}
            </button>
          </div>
        </form>
      )}

      <table className="ui-table">
        <thead>
          <tr>
            <th className="col-date">From</th>
            <th className="col-date">To</th>
            <th className="is-num">Hours / week</th>
            <th className="is-num">Annual, weeks</th>
            <th className="is-num">Sick, days</th>
            <th>Reason</th>
            <th />
            <th className="ui-table__actions" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {newestFirst.map((row) => (
            <tr key={row.key}>
              <td>
                {row.from
                  ? formatDay(row.from)
                  : <span className="ui-table__muted">Start</span>}
              </td>
              <td>
                {row.to
                  ? formatDay(row.to)
                  : <span className="ui-table__na">—</span>}
              </td>
              <td className="is-num">{formatHoursShort(row.contracted_hours_per_week)}</td>
              <td className="is-num">{formatHoursShort(row.annual_leave_weeks_per_year)}</td>
              <td className="is-num">{formatHoursShort(row.sick_leave_days_per_year)}</td>
              <td>
                {row.saved
                  ? row.note || <span className="ui-table__na">—</span>
                  // Once a later row ends this stretch its pill reads "Ended",
                  // and nothing else would say it ran on the defaults.
                  : <span className="ui-table__muted">Standard terms, not set</span>}
              </td>
              <td><StatusPill label={row.status.label} tone={row.status.tone} /></td>
              <td className="ui-table__actions">
                {canEdit && (
                  <span className="row-actions">
                    <IconButton
                      icon={<EditIcon />}
                      label={row.saved ? 'Edit entitlement' : 'Set entitlement'}
                      size="sm"
                      onClick={() => openEdit(row)}
                    />
                    {row.saved && (
                      <IconButton
                        icon={<TrashIcon />}
                        label="Remove entitlement"
                        tone="danger"
                        size="sm"
                        onClick={() => row.saved && onDeleteEntitlement(row.saved)}
                      />
                    )}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <PanelHeading
        title="Opening balance"
        spaced="sm"
        hint="What they had already built up on the day leave starts counting. Leave taken before that day is not counted: it is already inside the opening figure. Saved with the Save button below."
      />
      <div className="pdm-grid pdm-grid--3">
        <Field label="Leave starts" htmlFor="leave-start" hint={startHint}>
          <TextInput
            id="leave-start"
            type="date"
            value={draft.accrual_start_date}
            disabled={disabled}
            onChange={(e) => onDraftChange({ accrual_start_date: e.target.value })}
          />
        </Field>
        <Field
          label="Opening annual leave, hours"
          htmlFor="leave-opening-annual"
          error={errors.opening_annual_hours}
          hint="Carries forward with the rest of their annual leave."
        >
          <TextInput
            id="leave-opening-annual"
            type="number"
            step="0.25"
            value={draft.opening_annual_hours}
            invalid={!!errors.opening_annual_hours}
            disabled={disabled}
            onChange={(e) => onDraftChange({ opening_annual_hours: e.target.value })}
          />
        </Field>
        <Field
          label="Opening sick leave, hours"
          htmlFor="leave-opening-sick"
          error={errors.opening_sick_hours}
          hint="Counts in the year leave starts only. Sick leave resets each 1 January."
        >
          <TextInput
            id="leave-opening-sick"
            type="number"
            step="0.25"
            value={draft.opening_sick_hours}
            invalid={!!errors.opening_sick_hours}
            disabled={disabled}
            onChange={(e) => onDraftChange({ opening_sick_hours: e.target.value })}
          />
        </Field>
      </div>
    </>
  );
};

export default LeaveEntitlementSection;
